import React from 'react';
import { Grid, IconButton, InputAdornment, TextField, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { FONT_SIZE } from '../agendaStyles';

type AreaSearchFieldProps = {
  value: string;
  /** Null while nothing is being searched for, so the count stays hidden. */
  resultCount: number | null;
  onChange: (value: string) => void;
};

/**
 * The counter's way in: a customer gives a name or an order number, and the pillars narrow to
 * wherever that booking is sitting. Browsing by area answers "where is there room"; this answers
 * "where is this person", which is the question being asked at the desk.
 */
export const AreaSearchField = ({ value, resultCount, onChange }: AreaSearchFieldProps) => {
  const { t } = useTranslation();

  return (
    <Grid container alignItems="center" gap={1.5} wrap="nowrap">
      <TextField
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={t('calendar.areas_view.search_placeholder')}
        sx={{ flex: 1, '& .MuiOutlinedInput-root': { minHeight: { xs: 44, md: 48 } } }}
        InputProps={{
          inputProps: { 'aria-label': t('calendar.areas_view.search_placeholder') },
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ fontSize: '18px', color: 'text.secondary' }} />
            </InputAdornment>
          ),
          endAdornment: value ? (
            <InputAdornment position="end">
              <IconButton size="small" aria-label={t('calendar.areas_view.search_clear')} onClick={() => onChange('')}>
                <CloseIcon sx={{ fontSize: '18px' }} />
              </IconButton>
            </InputAdornment>
          ) : undefined,
        }}
      />

      {resultCount !== null && (
        <Typography sx={{ fontSize: FONT_SIZE.detail, color: 'text.secondary', whiteSpace: 'nowrap' }}>
          {t('calendar.areas_view.search_result_count', { count: resultCount })}
        </Typography>
      )}
    </Grid>
  );
};
